import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StepStatus, EpisodeStatus, ScriptGenerationStatus, ScriptMode, AssetType, IProjectAsset, IUserModel, AssetGenerationStatus } from '@packages/global';
import { useEpisode } from '../hooks/useEpisode';
import { useScriptGenerationStore, SCRIPT_STEPS } from '../stores/scriptGenerationStore';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useScriptMode } from '../hooks/useScriptMode';
import { useLocalBackup } from '../hooks/useLocalBackup';
import { useModelConfig } from '../hooks/scriptGeneration/useModelConfig';
import { useDialog } from '../hooks/useDialog';
import { useToast } from '../stores/toastStore';
import { episodeApi, episodeScriptApi, modelApi } from '../services/user';
import { projectAssetApi } from '../services/user/projectAsset.api';

// 拆分出的 hooks
import { useScriptPageState } from '../hooks/scriptGeneration/useScriptPageState';
import { useScriptWebSocket } from '../hooks/scriptGeneration/useScriptWebSocket';
import { useScriptActions } from '../hooks/scriptGeneration/useScriptActions';
import { useStoryboardActions } from '../hooks/scriptGeneration/useStoryboardActions';
import { useChatPanelApply } from '../hooks/scriptGeneration/useChatPanelApply';
import { useImageGenerationWebSocket } from '../hooks/useImageGenerationWebSocket';
import { useAssetGenerationWebSocket } from '../hooks/useAssetGenerationWebSocket';

// Layout Components
import { PageHeader, LeftNavigationPanel, ContentPanel } from '../components/ScriptGeneration/layout';

// 渲染组件
import StepContentRenderer from '../components/ScriptGeneration/sections/StepContentRenderer';
import ChatPanelRenderer from '../components/ScriptGeneration/sections/ChatPanelRenderer';

// Modals
import { GenerateStoryboardModal, MissingReferencesModal } from '../components/ScriptGeneration/common';
import ProjectAssetSelector from '../components/projectAssets/ProjectAssetSelector';
import { PlusIcon, SparklesIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import CompactModelSelector from '../components/common/form/CompactModelSelector';
import { useCoinStore } from '../stores/coinStore';

export default function ScriptGenerationPage() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['script', 'common']);
  const dialog = useDialog();
  const toast = useToast();

  // 导入主体弹窗状态（step 3=角色, 4=道具, 5=场景）
  const [importingStep, setImportingStep] = useState<3 | 4 | 5 | null>(null);

  // 剧集主体列表状态（用于批量生成）
  const [episodeAssets, setEpisodeAssets] = useState<IProjectAsset[]>([]);
  const [localAssetOverrides, setLocalAssetOverrides] = useState<Record<string, Partial<IProjectAsset>>>({});

  // 批量生成弹窗与提交状态
  const [batchConfirmOpen, setBatchConfirmOpen] = useState<boolean>(false);
  const [batchGenerating, setBatchGenerating] = useState<boolean>(false);
  const [batchModels, setBatchModels] = useState<IUserModel[]>([]);
  const [batchModelId, setBatchModelId] = useState<string>('');
  const batchModelsLoadedRef = useRef<boolean>(false);

  // 加载剧集主体列表
  const loadEpisodeAssets = useCallback(async () => {
    if (!episodeId) return;
    try {
      const result = await projectAssetApi.getEpisodeAssets(episodeId);
      setEpisodeAssets(result);
    } catch {
      // 静默忽略错误
    }
  }, [episodeId]);

  // 确保批量生成模型列表已加载（带缓存）
  const ensureBatchModels = useCallback(async (): Promise<IUserModel[]> => {
    if (batchModelsLoadedRef.current) {
      return batchModels;
    }
    try {
      const models = await modelApi.getAllModels({ type: 'text2image' });
      setBatchModels(models);
      setBatchModelId(models[0]?.id || '');
      batchModelsLoadedRef.current = true;
      return models;
    } catch {
      toast.error(t('script:batchGenerate.noModelsAvailable'));
      return [];
    }
  }, [batchModels, t, toast]);

  // 批量生成按钮点击处理（任务 4.1 将完整实现）
  const handleBatchGenerateClick = useCallback(async () => {
    const models = await ensureBatchModels();
    if (models.length === 0) return;
    setBatchConfirmOpen(true);
  }, [ensureBatchModels]);

  // 本地更新单个主体（用于批量生成状态同步）
  const updateAssetLocal = useCallback((assetId: string, updates: Partial<IProjectAsset>) => {
    setLocalAssetOverrides(prev => ({
      ...prev,
      [assetId]: { ...prev[assetId], ...updates },
    }));
  }, []);

  // 合并 episodeAssets 与本地覆盖（用于实时反映生成状态）
  const mergedEpisodeAssets = useMemo(
    () => episodeAssets.map(asset => ({ ...asset, ...localAssetOverrides[asset.id] })),
    [episodeAssets, localAssetOverrides]
  );

  const importAssetType = importingStep === 3 ? AssetType.CHARACTER
    : importingStep === 4 ? AssetType.PROP
      : importingStep === 5 ? AssetType.SCENE
        : undefined;

  const handleImportAsset = async (asset: any, value: string) => {
    if (!script?.id || !episode?.id) return;
    try {
      // 新逻辑：直接创建/关联 ProjectAsset 到当前剧集
      await projectAssetApi.createProjectAsset({
        projectId: episode.projectId,
        episodeId: episode.id,
        assetType: asset.assetType,
        name: asset.name,
        prompt: asset.prompt,
        conceptImageUrl: asset.conceptImageUrl,
        voiceReference: asset.voiceReference,
        voiceModelId: asset.voiceModelId,
        description: asset.description,
        location: asset.location,
      });
      await handleRefreshScript();
    } catch (e) {
      console.error('导入主体失败', e);
    }
    setImportingStep(null);
  };

  // 剧点余额
  const { account } = useCoinStore();
  const coinBalance = account?.balance ?? 0;

  // UI 状态
  const state = useScriptPageState();
  const [isEpisodeTitleSaving, setIsEpisodeTitleSaving] = useState(false);

  // Episode & Script 数据
  const { episode, loading: episodeLoading, refreshEpisode } = useEpisode(episodeId);
  const {
    script, steps, loading: scriptLoading, error: scriptError,
    loadScript, getStepData, updateStepData, saveStepData,
    refreshScript, markStepInProgress, markStepComplete
  } = useScriptGenerationStore();

  // 包装 refreshScript：刷新脚本后同步重新加载主体列表
  const handleRefreshScript = useCallback(async () => {
    await refreshScript();
    await loadEpisodeAssets();
  }, [refreshScript, loadEpisodeAssets]);

  // 步骤导航
  const { currentStep, currentStepInfo, stepsWithStatus, progress, navigateToStep } = useStepNavigation();

  // 脚本模式
  const { mode: scriptMode, actionVerb } = useScriptMode(script);

  // 模型配置
  const modelConfig = useModelConfig(episode);

  // scriptId
  const scriptId = episode?.scriptId || episode?.activeScriptId;

  // 本地备份
  const { backupStepDebounced, restoreStep, clearStepBackup, hasUnrestoredBackups } = useLocalBackup(scriptId);

  // WebSocket 订阅（页面级，避免步骤切换时重复订阅/取消）
  useImageGenerationWebSocket();

  // 主体生成 WebSocket（批量生成实时状态更新）
  // 使用 ref 去重，防止同一 assetId 的 completed/failed 回调被重复触发（toast 只显示一次）
  const toastShownAssetIdsRef = useRef<Set<string>>(new Set());
  useAssetGenerationWebSocket({
    onAssetCompleted: (assetId: string, imageUrl?: string, assetType?: AssetType) => {
      updateAssetLocal(assetId, {
        generationStatus: AssetGenerationStatus.COMPLETED,
        ...(imageUrl ? { conceptImageUrl: imageUrl } : {}),
      });
      if (!toastShownAssetIdsRef.current.has(assetId)) {
        toastShownAssetIdsRef.current.add(assetId);
        const typeKey = assetType
          ? `script:batchGenerate.imageGenCompleted_${assetType}`
          : null;
        const msg = typeKey && i18n.exists(typeKey)
          ? t(typeKey)
          : t('script:batchGenerate.imageGenCompleted');
        toast.success(msg);
        // 延迟清理，避免短时间内同一 assetId 再次触发
        setTimeout(() => toastShownAssetIdsRef.current.delete(assetId), 5000);
      }
    },
    onAssetFailed: (assetId: string, error: string, assetType?: AssetType) => {
      updateAssetLocal(assetId, { generationStatus: AssetGenerationStatus.FAILED });
      if (!toastShownAssetIdsRef.current.has(assetId)) {
        toastShownAssetIdsRef.current.add(assetId);
        const typeKey = assetType
          ? `script:batchGenerate.imageGenFailed_${assetType}`
          : null;
        const msg = typeKey && i18n.exists(typeKey)
          ? t(typeKey, { error })
          : t('script:batchGenerate.imageGenFailed', { error });
        toast.error(msg);
        setTimeout(() => toastShownAssetIdsRef.current.delete(assetId), 5000);
      }
    },
    onProgressUpdate: (data) => {
      // 将 WebSocket 状态映射到 AssetGenerationStatus
      const statusMap: Record<string, AssetGenerationStatus> = {
        'pending': AssetGenerationStatus.PENDING,
        'submitted': AssetGenerationStatus.GENERATING,
        'processing': AssetGenerationStatus.GENERATING,
        'generating': AssetGenerationStatus.GENERATING,
        'completed': AssetGenerationStatus.COMPLETED,
        'failed': AssetGenerationStatus.FAILED,
      };
      const mappedStatus = statusMap[data.status?.toLowerCase()] ?? AssetGenerationStatus.GENERATING;
      updateAssetLocal(data.assetId, {
        generationStatus: mappedStatus,
        ...(data.imageUrl && mappedStatus === AssetGenerationStatus.COMPLETED
          ? { conceptImageUrl: data.imageUrl }
          : {}),
      });
    },
  });

  useScriptWebSocket({
    scriptId, episodeId,
    setIsGenerating: state.setIsGenerating,
    setIsStep1Generating: state.setIsStep1Generating,
    setStep1Progress: state.setStep1Progress,
    setIsFailed: state.setIsFailed,
    setErrorMessage: state.setErrorMessage,
    setShotsGeneratingProgress: state.setShotsGeneratingProgress,
    setNewShotIndices: state.setNewShotIndices,
    markStepInProgress, markStepComplete,
    refreshScript, refreshEpisode,
    refreshEpisodeAssets: loadEpisodeAssets,
  });

  // 脚本操作 handlers
  const actions = useScriptActions({
    scriptId,
    currentStep,
    currentStepName: currentStepInfo?.name,
    actionVerb,
    setIsGenerating: state.setIsGenerating,
    setIsFailed: state.setIsFailed,
    setErrorMessage: state.setErrorMessage,
    markStepInProgress,
    navigateToStep,
    refreshScript,
    saveStepData,
    updateStepData,
    backupStepDebounced,
    clearStepBackup,
    setIsEditing: state.setIsEditing,
    currentGenerationStep: script?.currentGenerationStep,
  });

  // 分镜操作 handlers
  const storyboard = useStoryboardActions({
    script,
    episodeId,
    isRegenerateMode: state.isRegenerateMode,
    setIsRegenerateMode: state.setIsRegenerateMode,
    setIsProcessing: state.setIsProcessing,
    setIsFailed: state.setIsFailed,
    setErrorMessage: state.setErrorMessage,
    setShowStoryboardModal: state.setShowStoryboardModal,
    setShowMissingRefsModal: state.setShowMissingRefsModal,
    setMissingRefs: state.setMissingRefs,
    setPendingRegenerateMode: state.setPendingRegenerateMode,
    pendingRegenerateMode: state.pendingRegenerateMode,
    episodeAssets: mergedEpisodeAssets,
  });

  // Chat panel apply handlers
  const chatApply = useChatPanelApply({
    scriptId,
    script,
    currentStep,
    refreshScript,
    episodeId,
    projectId: episode?.projectId,
    refreshEpisodeAssets: loadEpisodeAssets,
  });

  // 自动启动生成（IDLE 状态）—— MV 模式与其他模式统一处理
  useEffect(() => {
    if (!script?.id || script.generationStatus !== ScriptGenerationStatus.IDLE) return;
    if (state.startGenerationCalledRef.current) return;

    if (state.startGenerationTimeoutRef.current) clearTimeout(state.startGenerationTimeoutRef.current);

    state.startGenerationTimeoutRef.current = setTimeout(async () => {
      if (state.startGenerationCalledRef.current) return;
      state.startGenerationCalledRef.current = true;
      state.setIsStep1Generating(true);
      try {
        await episodeScriptApi.startGeneration(script.id);
        await handleRefreshScript();
      } catch (err: any) {
        state.setIsFailed(true);
        state.setErrorMessage(err.message || t('status.starting'));
        toast.error(err.message || t('status.starting'));
        state.startGenerationCalledRef.current = false;
      }
    }, 500);

    return () => { if (state.startGenerationTimeoutRef.current) clearTimeout(state.startGenerationTimeoutRef.current); };
  }, [script?.id, script?.generationStatus]);

  // scriptId 变化时重置自动启动标记
  useEffect(() => {
    state.startGenerationCalledRef.current = false;
    state.setIsStep1Generating(false);
    state.setStep1Progress({});
  }, [scriptId]);

  // 加载脚本
  useEffect(() => {
    if (scriptId) loadScript(scriptId);
  }, [scriptId, loadScript]);

  // episode 加载完成后触发主体列表加载
  useEffect(() => {
    if (episode?.id) {
      loadEpisodeAssets();
    }
  }, [episode?.id, loadEpisodeAssets]);

  // 同步脚本生成状态到 UI
  useEffect(() => {
    const status = script?.generationStatus;
    if (status === ScriptGenerationStatus.FAILED) {
      state.setIsFailed(true);
      state.setIsGenerating(false);
      state.setIsStep1Generating(false);
      state.setErrorMessage(script?.generationErrorMessage || t('status.failed'));
    } else if (status === ScriptGenerationStatus.PROCESSING) {
      state.setIsGenerating(true);
      state.setIsFailed(false);
      // step1 已完成时确保不显示 loading（页面刷新后 WebSocket 消息不会重发）
      const step1Done = steps.some(s => s.stepNumber === 1 && s.status === StepStatus.COMPLETED);
      if (step1Done) state.setIsStep1Generating(false);
    } else if (status === ScriptGenerationStatus.COMPLETED) {
      state.setIsGenerating(false);
      state.setIsFailed(false);
      state.setIsStep1Generating(false);
    } else if (status === ScriptGenerationStatus.IDLE) {
      state.setIsGenerating(false);
      state.setIsFailed(false);
    } else {
      const hasInProgress = steps.some(s => s.status === StepStatus.IN_PROGRESS);
      const hasCompleted = steps.some(s => s.status === StepStatus.COMPLETED);
      const allCompleted = steps.every(s => s.status === StepStatus.COMPLETED);
      if (hasInProgress) { state.setIsGenerating(true); state.setIsFailed(false); }
      else if (hasCompleted && !allCompleted) { state.setIsFailed(true); state.setIsGenerating(false); state.setErrorMessage(t('status.interrupted')); }
    }
  }, [script?.generationStatus, script?.generationErrorMessage, steps]);

  // 检查并恢复本地备份
  useEffect(() => {
    if (!scriptId || !hasUnrestoredBackups()) return;
    const restored = restoreStep(currentStep);
    if (!restored) return;
    dialog.confirm(t('restore.confirm'), t('restore.title')).then((confirmed) => {
      if (confirmed) { updateStepData(currentStep, restored); toast.success(t('restore.success')); }
      else clearStepBackup(currentStep);
    });
  }, [scriptId, currentStep]);

  // 当前步骤数据 - 只依赖 currentStep 和 steps，不依赖 getStepData 函数引用
  const currentStepData = useMemo(() => getStepData(currentStep), [currentStep, steps]);

  // 当前步骤对应类型的主体列表（Step 3=角色, 4=道具, 5=场景）
  const currentStepAssets = useMemo(() => {
    if (![3, 4, 5].includes(currentStep)) return [];
    const typeMap: Record<number, AssetType> = {
      3: AssetType.CHARACTER,
      4: AssetType.PROP,
      5: AssetType.SCENE,
    };
    return mergedEpisodeAssets.filter(asset => asset.assetType === typeMap[currentStep]);
  }, [currentStep, mergedEpisodeAssets]);

  // 待生成主体列表：conceptImageUrl 和 frontViewUrl 均为空的记录
  const pendingAssets = useMemo(
    () => currentStepAssets.filter(asset => !asset.conceptImageUrl && !asset.frontViewUrl),
    [currentStepAssets]
  );

  // 批量生成确认处理：逐个提交生成任务并汇总结果
  const handleBatchGenerateConfirm = useCallback(async () => {
    setBatchConfirmOpen(false);
    setBatchGenerating(true);
    let successCount = 0;
    let failCount = 0;
    for (const asset of pendingAssets) {
      try {
        await projectAssetApi.generateAsset(asset.id, batchModelId);
        updateAssetLocal(asset.id, { generationStatus: AssetGenerationStatus.PENDING });
        successCount++;
      } catch {
        failCount++;
      }
    }
    if (failCount === 0) {
      toast.success(t('script:batchGenerate.submittedTasks', { count: successCount }));
    } else if (successCount > 0) {
      toast.warning(t('script:batchGenerate.partialSuccess', { success: successCount, fail: failCount }));
    } else {
      toast.error(t('script:batchGenerate.allFailed'));
    }
    setBatchGenerating(false);
  }, [pendingAssets, batchModelId, updateAssetLocal, t, toast]);

  // 步骤点击回调（必须在早返回之前定义）
  const handleStepClick = useCallback((step: number) => navigateToStep(step), [navigateToStep]);

  const handleEpisodeTitleSave = useCallback(async (title: string) => {
    if (!episode?.id) return;

    setIsEpisodeTitleSaving(true);
    try {
      await episodeApi.updateEpisode(episode.id, { title });
      await refreshEpisode();
      toast.success('标题已更新');
    } catch (error: any) {
      toast.error(error?.message || '标题更新失败');
    } finally {
      setIsEpisodeTitleSaving(false);
    }
  }, [episode?.id, refreshEpisode, toast]);

  // 格式化步骤列表
  const formattedSteps = stepsWithStatus.map(s => ({
    stepNumber: s.stepNumber,
    name: s.name,
    status: s.status,
    data: getStepData(s.stepNumber)
  }));

  // 加载状态
  if (episodeLoading || scriptLoading) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center">
        <div className="text-gray-50">{t('loading')}</div>
      </div>
    );
  }

  if (!scriptId) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">{t('noScript')}</div>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark cursor-pointer">{t('back')}</button>
        </div>
      </div>
    );
  }

  if (scriptError) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center">
        <div className="text-error">{scriptError}</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-bg-page flex flex-col overflow-hidden">
      <PageHeader
        episodeName={episode?.title || script?.title || t('page.defaultTitle')}
        onEpisodeNameSave={handleEpisodeTitleSave}
        isEpisodeNameSaving={isEpisodeTitleSaving}
        activeTab="script"
        onBack={() => actions.handleBack(episode?.projectId)}
        onTabChange={(tab) => actions.handleTabChange(tab, episode?.id)}
        editorDisabled={episode?.status === EpisodeStatus.DRAFT}
      />

      <div className="flex-1 flex overflow-hidden bg-bg-page">
        <LeftNavigationPanel
          requirement={script?.originalPrompt || script?.prompt || ''}
          steps={formattedSteps}
          activeStep={currentStep}
          onStepClick={handleStepClick}
          onRegenerateStep={actions.handleRegenerateStep}
          onGenerateStoryboard={storyboard.handleGenerateStoryboard}
          onViewVideo={() => actions.handleViewVideo(episode?.id)}
          onRegenerateStoryboard={storyboard.handleRegenerateStoryboard}
          onRetry={actions.handleRetry}
          onForceRetry={actions.handleForceRetry}
          allStepsCompleted={progress.allCompleted}
          isGenerating={state.isGenerating}
          isProcessing={state.isProcessing}
          isFailed={state.isFailed}
          errorMessage={state.errorMessage}
          scriptMode={scriptMode}
          episodeStatus={episode?.status}
          uploadedScriptContent={script?.uploadedScriptContent}
          uploadedFileUrl={script?.uploadedFileUrl}
        />

        <div className="flex-1 flex gap-4 p-4 overflow-hidden">
          <ContentPanel
            stepNumber={currentStep}
            stepName={currentStepInfo?.name || SCRIPT_STEPS[currentStep - 1]?.name || ''}
            stepStatus={currentStepInfo?.status || StepStatus.PENDING}
            isLoading={scriptLoading}
            actions={
              [3, 4, 5].includes(currentStep) && script?.id ? (
                <div className="flex items-center gap-2">
                  {/* 批量生成按钮 */}
                  <button
                    onClick={handleBatchGenerateClick}
                    disabled={pendingAssets.length === 0 || batchGenerating}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-bg-elevated hover:bg-bg-surface border border-border-subtle text-gray-300 hover:text-gray-50 text-xs transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <SparklesIcon className="w-3.5 h-3.5" />
                    {pendingAssets.length > 0
                      ? t('script:batchGenerate.button', { count: pendingAssets.length })
                      : t('script:batchGenerate.buttonDisabled')}
                  </button>
                  {/* 导入主体按钮 */}
                  <button
                    onClick={() => setImportingStep(currentStep as 3 | 4 | 5)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-bg-elevated hover:bg-bg-surface border border-border-subtle text-gray-300 hover:text-gray-50 text-xs transition-colors cursor-pointer"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    {currentStep === 3 ? t('import.character') : currentStep === 4 ? t('import.prop') : t('import.scene')}
                  </button>
                </div>
              ) : currentStep === 6 && script?.id ? (
                // Step 6 分镜脚本：重新生成按钮（适用于剧本已 COMPLETED 后单独重做分镜）
                <button
                  onClick={actions.handleRegenerateStep}
                  disabled={state.isGenerating}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-bg-elevated hover:bg-bg-surface border border-border-subtle text-gray-300 hover:text-gray-50 text-xs transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${state.isGenerating ? 'animate-spin' : ''}`} />
                  重新生成分镜
                </button>
              ) : undefined
            }
          >
            <StepContentRenderer
              currentStep={currentStep}
              currentStepData={currentStepData}
              currentStepStatus={currentStepInfo?.status}
              currentStepName={currentStepInfo?.name}
              scriptId={scriptId}
              script={script}
              episode={episode}
              isEditing={state.isEditing}
              isStep1Generating={state.isStep1Generating}
              step1Progress={state.step1Progress}
              shotsGeneratingProgress={state.shotsGeneratingProgress}
              newShotIndices={state.newShotIndices}
              onClearNewShotIndices={() => state.setNewShotIndices([])}
              onEdit={() => state.setIsEditing(true)}
              onCancelEdit={() => state.setIsEditing(false)}
              onSave={actions.handleStepDataSave}
              onRegenerate={actions.handleRegenerateStep}
              onChange={actions.handleStepDataChange}
              onUpdate={handleRefreshScript}
              episodeAssets={mergedEpisodeAssets}
            />
          </ContentPanel>

          <div className="w-[360px] flex-shrink-0">
            <ChatPanelRenderer
              currentStep={currentStep}
              projectId={episode?.projectId}
              scriptId={scriptId}
              currentStepData={currentStepData}
              onSimpleApply={chatApply.handleSimpleApply}
              onCharactersApply={chatApply.handleCharactersApply}
              onPropsApply={chatApply.handlePropsApply}
              onScenesApply={chatApply.handleScenesApply}
              onShotsApply={chatApply.handleShotsApply}
              episodeAssets={mergedEpisodeAssets}
            />
          </div>
        </div>
      </div>

      <GenerateStoryboardModal
        isOpen={state.showStoryboardModal}
        onClose={() => state.setShowStoryboardModal(false)}
        onConfirm={storyboard.handleStoryboardConfirm}
        isRegenerate={state.isRegenerateMode}
        initialConfig={{
          text2imageModelId: modelConfig.text2imageModelId,
          imageEditModelId: modelConfig.imageEditModelId,
          text2speechModelId: modelConfig.text2speechModelId,
          aspectRatio: modelConfig.aspectRatio,
          generateImages: true,
          generateEndFrame: modelConfig.generateEndFrame,
        }}
        text2imageModels={modelConfig.text2imageModels}
        imageEditModels={modelConfig.imageEditModels}
        text2speechModels={modelConfig.text2speechModels}
        shotCount={script?.shots?.length || 0}
        resolution={episode?.resolution}
        isProcessing={state.isProcessing}
      />

      <MissingReferencesModal
        isOpen={state.showMissingRefsModal}
        onClose={() => state.setShowMissingRefsModal(false)}
        onConfirm={storyboard.handleMissingRefsConfirm}
        missingRefs={state.missingRefs}
      />

      <ProjectAssetSelector
        isOpen={importingStep !== null}
        projectId={episode?.projectId}
        onSelect={handleImportAsset}
        onClose={() => setImportingStep(null)}
        initialAspect="image"
        initialAssetType={importAssetType}
      />

      {/* 批量生成确认弹窗 */}
      {batchConfirmOpen && (() => {
        const selectedModel = batchModels.find(m => m.id === batchModelId);
        const totalCost = (selectedModel?.coinPrice || 0) * pendingAssets.length;
        const balance = coinBalance;
        const isInsufficient = (selectedModel?.coinPrice || 0) > 0 && balance < totalCost;
        const currentStepTypeName = currentStep === 3 ? t('script:import.character') : currentStep === 4 ? t('script:import.prop') : t('script:import.scene');

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-bg-elevated border border-border-default rounded-2xl p-6 w-full max-w-sm shadow-xl">
              {/* 标题 */}
              <div className="flex items-center gap-2 mb-4">
                <SparklesIcon className="w-5 h-5 text-primary" />
                <h3 className="text-base font-semibold text-gray-50">{t('script:batchGenerate.title')}</h3>
              </div>
              {/* 描述 */}
              <p
                className="text-sm text-gray-300 mb-4"
                dangerouslySetInnerHTML={{
                  __html: t('script:batchGenerate.description', { count: pendingAssets.length, type: currentStepTypeName })
                }}
              />
              {/* 模型选择 */}
              <div className="mb-4">
                <CompactModelSelector
                  label={t('script:batchGenerate.selectModel')}
                  models={batchModels}
                  value={batchModelId}
                  onChange={setBatchModelId}
                />
              </div>
              {/* 费用估算（仅当 coinPrice > 0 时显示） */}
              {(selectedModel?.coinPrice || 0) > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{t('script:batchGenerate.estimatedCost')}</span>
                    <span className="text-gray-50">{totalCost} {t('script:batchGenerate.coins')}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('script:batchGenerate.balance', { amount: balance })}
                  </div>
                  {isInsufficient && (
                    <div className="flex items-center gap-1.5 text-xs text-error">
                      <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                      <span>{t('script:batchGenerate.insufficientBalance')}</span>
                    </div>
                  )}
                </div>
              )}
              {/* 按钮行 */}
              <div className="flex items-center gap-3 justify-end">
                <button
                  onClick={() => setBatchConfirmOpen(false)}
                  className="px-4 py-2 rounded-lg bg-bg-elevated hover:bg-bg-surface border border-border-subtle text-gray-300 hover:text-gray-50 text-sm transition-colors cursor-pointer"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleBatchGenerateConfirm}
                  disabled={isInsufficient}
                  className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('script:batchGenerate.confirmGenerate')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
